// src/components/admin/UserProfile.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminShell } from './Sidebar';
import UserClientsManager from './UserClientsManager';
import { LoadingState } from '../ui/CrmPage';

const GENDER_OPTIONS = [
  { id: '', label: '— не указано —' },
  { id: 'male', label: 'Мужской' },
  { id: 'female', label: 'Женский' },
  { id: 'other', label: 'Другое' },
];

const initials = (fn = '', ln = '') => {
  const a = [fn, ln].filter(Boolean).map((s) => s.trim()[0]?.toUpperCase());
  return a.join('') || 'U';
};
const formatPhone = (raw) => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('8')) {
    const p = digits.split('');
    return `+7 ${p.slice(1, 4).join('')} ${p.slice(4, 7).join('')} ${p.slice(7, 9).join('')} ${p.slice(9).join('')}`;
  }
  return raw;
};

export default function UserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState('');

  const [user, setUser] = useState({ id: '', login: '', phone_number: '', id_role: '', is_active: true });
  const [roles, setRoles] = useState([]);
  const [profile, setProfile] = useState({ firstname: '', secondname: '', lastname: '', email: '', gender: '' });
  const [hasProfile, setHasProfile] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true); setError('');

        const rRes = await fetch(`${apiBase}v1/roles?sortBy=id&orderBy=asc`, { signal: ctrl.signal });
        if (rRes.ok) setRoles(await rRes.json() || []);

        const uRes = await fetch(`${apiBase}v1/users/${id}`, { signal: ctrl.signal });
        if (!uRes.ok) {
          const t = await uRes.text().catch(() => '');
          throw new Error(`Users HTTP ${uRes.status} ${uRes.statusText} ${t}`.trim());
        }
        const u = await uRes.json();
        setUser({
          id: String(u.id ?? id),
          login: u.login ?? '',
          phone_number: u.phone_number ?? '',
          id_role: String(u.id_role ?? ''),
          is_active: Boolean(u.is_active),
        });

        const pRes = await fetch(`${apiBase}v1/profiles/${id}`, { signal: ctrl.signal });
        if (pRes.status === 404) {
          setHasProfile(false);
        } else if (!pRes.ok) {
          const t = await pRes.text().catch(() => '');
          throw new Error(`Profiles HTTP ${pRes.status} ${pRes.statusText} ${t}`.trim());
        } else {
          const p = await pRes.json();
          setHasProfile(true);
          setProfile({
            firstname: p.firstname ?? '',
            secondname: p.secondname ?? '',
            lastname: p.lastname ?? '',
            email: p.email ?? '',
            gender: p.gender ?? '',
          });
        }
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, id]);

  const onUserChange    = (e) => { const { name, value } = e.target; setUser((s) => ({ ...s, [name]: value })); };
  const onProfileChange = (e) => { const { name, value } = e.target; setProfile((s) => ({ ...s, [name]: value })); };

  const onResetPassword = async () => {
    setSaveError(''); setSaveOk('');
    try {
      const res = await fetch(`${apiBase}v1/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_password: true }),
      });
      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(`Сброс пароля: HTTP ${res.status} ${t}`.trim());
      }
      const data = await res.json();
      const temp = data?.temp_password;
      setSaveOk(temp ? `Временный пароль: ${temp}` : 'Пароль сброшен');
    } catch (e) {
      setSaveError(e.message || 'Не удалось сбросить пароль');
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveError(''); setSaveOk('');
    try {
      const uRes = await fetch(`${apiBase}v1/users/${user.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          login: user.login,
          phone_number: user.phone_number,
          is_active: user.is_active,
          id_role: Number.parseInt(user.id_role || 0, 10) || null,
        }),
      });
      if (!uRes.ok) {
        const t = await uRes.text().catch(() => '');
        throw new Error(`Users save: HTTP ${uRes.status} ${t}`.trim());
      }
      const method = hasProfile ? 'PUT' : 'POST';
      const url = hasProfile ? `${apiBase}v1/profiles/${user.id}` : `${apiBase}v1/profiles`;
      const payload = {
        firstname: profile.firstname || '',
        secondname: profile.secondname || '',
        lastname: profile.lastname || '',
        gender: profile.gender || null,
        email: profile.email || null,
        photo: null,
        id_users: Number(user.id),
      };
      const pRes = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!pRes.ok) {
        const t = await pRes.text().catch(() => '');
        throw new Error(`Profiles save: HTTP ${pRes.status} ${t}`.trim());
      }
      setHasProfile(true);
      setSaveOk('Изменения сохранены');
    } catch (e) {
      setSaveError(e.message || 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminShell title="Профиль пользователя" subtitle="Загрузка…">
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <LoadingState text="Загружаем профиль…" />
          </div>
        </div>
      </AdminShell>
    );
  }
  if (error) {
    return (
      <AdminShell title="Профиль пользователя" subtitle="Ошибка">
        <div className="crmpage crmpage--embedded">
          <div className="crmpage__main">
            <div className="crmpage__alert crmpage__alert--error">{error}</div>
            <div className="adminform__actions">
              <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Назад</button>
            </div>
          </div>
        </div>
      </AdminShell>
    );
  }

  const fullName = [profile.lastname, profile.firstname, profile.secondname].filter(Boolean).join(' ') || '—';
  const roleLabel = roles.find((r) => String(r.id) === String(user.id_role))?.name
    || (user.id_role ? `Role #${user.id_role}` : '—');

  return (
    <AdminShell
      breadcrumbs="Пользователи"
      title="Профиль пользователя"
      subtitle="Карточка сотрудника и изменение данных"
      actions={
        <>
          <button type="button" className="adminbtn" onClick={() => navigate(-1)}>Назад</button>
          <button type="button" className="adminbtn adminbtn--primary" disabled={saving} onClick={onSave}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          {!hasProfile && (
            <div className="crmpage__alert crmpage__alert--warning">
              Профиль ещё не создан. Он будет создан при сохранении.
            </div>
          )}
          {!!saveError && <div className="crmpage__alert crmpage__alert--error">{saveError}</div>}
          {!!saveOk && <div className="crmpage__alert" style={{ background: 'var(--success-bg)', borderColor: 'var(--success-border)', color: '#0F5B3C' }}>{saveOk}</div>}

          <div className="adminprofile__header">
            <div className="adminprofile__avatar">{initials(profile.firstname, profile.lastname)}</div>
            <div className="adminprofile__head-text">
              <h2 className="adminprofile__name">{fullName}</h2>
              <div className="adminprofile__meta">
                <span>{roleLabel}</span>
                <span className="adminprofile__meta-sep" />
                <span>@{user.login || '—'}</span>
                <span className="adminprofile__meta-sep" />
                <span>{formatPhone(user.phone_number) || '—'}</span>
                <span className={`crmpage__status crmpage__status--${user.is_active ? 'success' : 'neutral'}`}>
                  {user.is_active ? 'Активен' : 'Отключён'}
                </span>
              </div>
            </div>
            <div className="adminprofile__head-actions">
              <button type="button" className="adminbtn" onClick={onResetPassword}>Сбросить пароль</button>
            </div>
          </div>

          <form className="adminform" onSubmit={onSave}>
            <section className="adminform__card">
              <div className="adminform__section-title">Личные данные</div>
              <div className="adminform__grid adminform__grid--3">
                <label className="adminform__field">
                  <span className="adminform__label">Фамилия</span>
                  <input className="adminform__input" name="lastname" value={profile.lastname} onChange={onProfileChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Имя</span>
                  <input className="adminform__input" name="firstname" value={profile.firstname} onChange={onProfileChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Отчество</span>
                  <input className="adminform__input" name="secondname" value={profile.secondname} onChange={onProfileChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">E-mail</span>
                  <input type="email" className="adminform__input" name="email" value={profile.email} onChange={onProfileChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Пол</span>
                  <select className="adminform__select" name="gender" value={profile.gender} onChange={onProfileChange}>
                    {GENDER_OPTIONS.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                  </select>
                </label>
              </div>
            </section>

            <section className="adminform__card">
              <div className="adminform__section-title">Учётная запись</div>
              <div className="adminform__grid">
                <label className="adminform__field">
                  <span className="adminform__label">Логин</span>
                  <input className="adminform__input" name="login" value={user.login} onChange={onUserChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Телефон</span>
                  <input className="adminform__input" name="phone_number" value={user.phone_number} onChange={onUserChange} />
                </label>
                <label className="adminform__field">
                  <span className="adminform__label">Роль</span>
                  <select className="adminform__select" name="id_role" value={user.id_role} onChange={onUserChange}>
                    <option value="">— выберите —</option>
                    {roles.map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                  </select>
                </label>
                <label className="adminform__field" style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingTop: 28 }}>
                  <input
                    type="checkbox"
                    className="crmpage__check"
                    checked={user.is_active}
                    onChange={(e) => setUser((s) => ({ ...s, is_active: e.target.checked }))}
                  />
                  <span className="adminform__label" style={{ margin: 0 }}>
                    {user.is_active ? 'Учётная запись активна' : 'Учётная запись отключена'}
                  </span>
                </label>
              </div>
            </section>
          </form>

          <UserClientsManager userId={user.id} />
        </div>
      </div>
    </AdminShell>
  );
}
